import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseMutationProps, UseQueryProps } from "@lib/types/Api";
import { Customer } from "@lib/types/Customer";
import {
  Button,
  Flex,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Switch,
  Typography,
  Upload,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BsCloudUpload } from "react-icons/bs";
import useIntl from "react-intl/lib/src/components/useIntl";
import type { UploadFile, UploadProps } from "antd";
import { fileToBase64, generateLogoFromText } from "@lib/helpers/FileHelper";
import { customerSchema as yupSchema } from "@lib/schemas/customer";
import { createYupSync } from "@lib/helpers/TableHelper";
import { healthCheckCrmDomain } from "@lib/helpers/UrlHelper";
import { convertNumericFields } from "@lib/types/General";
import { FormattedMessage } from "react-intl";
import { MdWarning } from "react-icons/md";
import { FaCheck, FaPencilAlt } from "react-icons/fa";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { useCustomerQueries } from "@lib/apiQueries";
import { useApi } from "@lib/hooks/APIHooks";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<Customer>;
  customer?: Customer;
  query?: UseQueryProps<Customer>;
};

const HEALTHCHECK_SUFFIX = {
  crm: "/api/v1/healthcheck",
  whatsapp: "/api/v1/admin/health-check",
};

export default function CustomerModal({
  openModal,
  toggleModal,
  mutationQuery,
  customer,
}: Props) {
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [domainChecking, setDomainChecking] = useState<boolean>(false);
  const [domainError, setDomainError] = useState<string | null>(null);
  const customerName = Form.useWatch("name", form);
  const domain = Form.useWatch("domain", form);
  const isWhatsappPlatform = Form.useWatch("isWhatsappPlatform", form);

  const { mutate, isPending } = mutationQuery;

  const { queriesCacheClear } = useApi();

  const initialValues = useMemo(() => {
    return customer
      ? convertNumericFields(customer)
      : ({
          name: "",
          domain: "",
          logoUrl: "",
          enabled: true,
          isClosed: false,
          isAutoBtcEnabled: false,
          isWhatsappPlatform: false,
          isMarketPlaceEnabled: false,
          aiServicesMargin: null,
        } as Customer);
  }, [customer]);

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
  }, [initialValues, form]);

  const handleClose = useCallback(() => {
    handleResetForm();
    toggleModal(false);
  }, [toggleModal]);

  useEffect(() => {
    if (!openModal) return;

    handleResetForm();

    if (customer?.logoUrl) {
      setFileList([
        {
          uid: "-1",
          name: "image.png",
          status: "done",
          url: customer?.logoUrl,
          response: customer?.logoUrl,
        },
      ]);
    } else {
      setFileList([]);
    }
  }, [openModal, customer, handleResetForm]);

  const onChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    if (newFileList.length > 0) {
      setFileList([newFileList[newFileList.length - 1]]);
    } else {
      setFileList([]);
    }
  };

  const [previewImg, setPreviewImg] = useState<string>();
  const onPreview = async (file: UploadFile) => {
    let src = file.url as string;
    if (!src) {
      src = await fileToBase64(file.originFileObj as File);
    }
    setPreviewImg(src);
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const handleGenerateLogo = useCallback(() => {
    if (!customerName || customerName.trim().length === 0) {
      return;
    }

    const generatedLogo = generateLogoFromText(customerName);
    if (generatedLogo) {
      form.setFieldValue("logoUrl", generatedLogo);
      setFileList([
        {
          uid: "-1",
          name: "generated-logo.png",
          status: "done",
          url: generatedLogo,
          response: generatedLogo,
        },
      ]);
    }
  }, [customerName, form]);

  const { customerNotifyStatusQuery } = useCustomerQueries({
    id: customer?.id,
    messages,
    queryKey: ["customer", customer?.id],
  });

  useEffect(() => {
    if (!domain || domain.trim().length === 0) {
      setDomainChecking(false);
      setDomainError(null);
      return;
    }

    setDomainChecking(true);
    healthCheckCrmDomain(
      domain,
      isWhatsappPlatform ? HEALTHCHECK_SUFFIX.whatsapp : HEALTHCHECK_SUFFIX.crm,
    )
      .then((result) => {
        if (result.status) {
          setDomainError(null);
        } else {
          setDomainError(result.message);
        }
      })
      .finally(() => {
        setDomainChecking(false);
      });
  }, [domain, isWhatsappPlatform]);

  return (
    <Modal
      title={
        <FormattedMessage
          id={customer ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.customer"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
      width={{
        xs: "90%",
        sm: "80%",
        md: "70%",
        lg: "70%",
        xl: "60%",
        xxl: "40%",
      }}
    >
      <Form<Customer>
        {...layout}
        form={form}
        name={"customer-modal"}
        style={{ maxWidth: 900, marginTop: 20 }}
        autoComplete="off"
        initialValues={{ enabled: true, ...customer }}
        onFinish={(values) => {
          mutate({ ...values, id: customer?.id });
          queriesCacheClear({ searchKeys: ["customer"] });
        }}
      >
        <Form.Item
          name="name"
          label={messages["common.customerName"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="domain"
          label={messages["common.domain"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input
            value={domain}
            addonAfter={
              <Flex align="center" gap={8}>
                <Typography.Text type="secondary">
                  {isWhatsappPlatform
                    ? HEALTHCHECK_SUFFIX.whatsapp
                    : HEALTHCHECK_SUFFIX.crm}
                </Typography.Text>

                {form.getFieldValue("domain")?.length == 0 || domainChecking ? (
                  <>
                    {domainChecking ? <Spin /> : <FaPencilAlt color="orange" />}
                  </>
                ) : (
                  <>
                    {!!domainError ? (
                      <MdWarning color="red" />
                    ) : (
                      <FaCheck color="green" />
                    )}
                  </>
                )}
              </Flex>
            }
          />
        </Form.Item>

        <Form.Item
          name="isWhatsappPlatform"
          label={messages["common.whatsappPlatform"] as string}
          extra={messages["common.whatsappPlatform.info"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
          valuePropName="checked"
        >
          <Switch
            checked
            checkedChildren={messages["common.enabled"] as string}
            unCheckedChildren={messages["common.disabled"] as string}
          />
        </Form.Item>

        <Form.Item
          name="enabled"
          label={messages["common.accountStatus"] as string}
          extra={messages["common.accountStatus.info"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
          valuePropName="checked"
        >
          <Switch
            checked
            checkedChildren={messages["common.enabled"] as string}
            unCheckedChildren={messages["common.disabled"] as string}
          />
        </Form.Item>

        <Form.Item
          label={messages["common.appStatus"] as string}
          extra={messages["common.appStatus.info"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Switch
            checkedChildren={messages["common.enabled"] as string}
            unCheckedChildren={messages["common.disabled"] as string}
            checked={!customer?.isClosed}
            onChange={async (checked) => {
              await customerNotifyStatusQuery.mutateAsync({
                id: customer.id,
                shouldClose: !checked,
              });
            }}
          />
        </Form.Item>

        <Form.Item
          name="isAutoBtcEnabled"
          label={messages["common.sendByteStatus"] as string}
          extra={messages["common.sendByteStatus.info"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
          valuePropName="checked"
        >
          <Switch
            checked
            checkedChildren={messages["common.enabled"] as string}
            unCheckedChildren={messages["common.disabled"] as string}
          />
        </Form.Item>

        <Form.Item
          name="isMarketPlaceEnabled"
          label={messages["common.marketPlaceStatus"] as string}
          extra={messages["common.marketPlaceStatus.info"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
          valuePropName="checked"
        >
          <Switch
            checked
            checkedChildren={messages["common.enabled"] as string}
            unCheckedChildren={messages["common.disabled"] as string}
          />
        </Form.Item>

        <Form.Item
          name="aiServicesMargin"
          label={messages["common.aiServicesMargin"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <InputNumber addonAfter={<Typography.Text>%</Typography.Text>} />
        </Form.Item>

        <Form.Item
          label={messages["common.logo"] as string}
          valuePropName="fileList"
          getValueFromEvent={normFile}
          name="logoUrl"
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input type="hidden" />
          {/* <ImgCrop rotationSlider> */}
          <Upload
            listType="picture-card"
            multiple={false}
            onChange={onChange}
            onPreview={onPreview}
            fileList={fileList}
            customRequest={async (props) => {
              const image = await fileToBase64(props.file as File);
              form.setFieldValue("logoUrl", image);
              props.onSuccess(image);
            }}
          >
            <button style={{ border: 0, background: "none" }} type="button">
              <BsCloudUpload />
              <div style={{ marginTop: 8 }}>
                {messages["common.upload"] as string}
              </div>
            </button>
          </Upload>
          {/* </ImgCrop> */}

          <Image
            width={200}
            style={{ display: "none" }}
            src=""
            preview={{
              visible: !!previewImg,
              src: previewImg,
              onVisibleChange: () => {
                setPreviewImg("");
              },
            }}
          />
          <div style={{ marginTop: 8 }}>
            <Button
              type="default"
              onClick={handleGenerateLogo}
              disabled={!customerName || customerName.trim().length === 0}
            >
              {(messages["common.generateLogo"] as string) || "Generate Logo"}
            </Button>
          </div>
        </Form.Item>

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            {!customer && (
              <ActionButton
                type="reset"
                onClick={handleResetForm}
                loading={isPending}
              />
            )}

            <ActionButton
              type={!!customer ? "save" : "add"}
              loading={isPending}
            />
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
